/** Apply document-level SEO / AEO / AGEO signals for storefront shells. */
export type SeoInput = {
  title: string
  description?: string
  canonicalUrl?: string
  siteName?: string
  locale?: string
  imageUrl?: string
  themeColor?: string
  address?: string
  telephone?: string
  email?: string
  openingHours?: string
  sameAs?: string[]
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (!content) return
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

function upsertLink(rel: string, href: string) {
  if (!href) return
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    document.head.appendChild(el)
  }
  el.href = href
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

export function applyDocumentSeo(input: SeoInput) {
  const title = (input.title || '').trim() || 'Site'
  const description = (input.description || '').trim()
  document.title = title

  upsertMeta('name', 'description', description)
  upsertMeta('name', 'robots', 'index,follow,max-image-preview:large')
  upsertMeta('name', 'googlebot', 'index,follow')
  // AEO / answer-engine friendly:
  upsertMeta('name', 'abstract', description)
  upsertMeta('name', 'subject', title)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', description)
  upsertMeta('property', 'og:site_name', input.siteName || title)
  if (input.locale) upsertMeta('property', 'og:locale', input.locale)
  if (input.imageUrl) upsertMeta('property', 'og:image', input.imageUrl)
  upsertMeta('name', 'twitter:card', input.imageUrl ? 'summary_large_image' : 'summary')
  upsertMeta('name', 'twitter:title', title)
  upsertMeta('name', 'twitter:description', description)
  if (input.themeColor) {
    upsertMeta('name', 'theme-color', input.themeColor)
    upsertMeta('name', 'msapplication-TileColor', input.themeColor)
  }
  upsertMeta('name', 'apple-mobile-web-app-capable', 'yes')
  upsertMeta('name', 'mobile-web-app-capable', 'yes')
  upsertMeta('name', 'apple-mobile-web-app-title', input.siteName || title)

  const canonical = input.canonicalUrl || (typeof location !== 'undefined' ? `${location.origin}/` : '')
  upsertLink('canonical', canonical)

  upsertJsonLd('mybo-ld-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: input.siteName || title,
    url: canonical,
    description,
    inLanguage: input.locale || 'zh-Hant',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${canonical.replace(/\/$/, '')}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  })

  // LocalBusiness when we have a street address (corp / shop contact seeds).
  const orgType = input.address ? 'LocalBusiness' : 'Organization'
  const org: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': orgType,
    name: input.siteName || title,
    url: canonical,
    description,
    ...(input.imageUrl ? { logo: input.imageUrl, image: input.imageUrl } : {}),
  }
  if (input.address) {
    org.address = buildPostalAddress(input.address, input.telephone)
  }
  if (input.telephone) org.telephone = input.telephone
  if (input.email) org.email = input.email
  if (input.openingHours) {
    org.openingHours = input.openingHours
    const specs = parseOpeningHoursSpecifications(input.openingHours)
    if (specs) org.openingHoursSpecification = specs
  }
  if (input.telephone || input.email) {
    org.contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      ...(input.telephone ? { telephone: input.telephone } : {}),
      ...(input.email ? { email: input.email } : {}),
      availableLanguage: [input.locale || 'zh-Hant', 'en'],
    }
  }
  if (input.sameAs && input.sameAs.length > 0) org.sameAs = input.sameAs
  upsertJsonLd('mybo-ld-org', org)

  // AGEO / answer-engine: speakable summary for the primary page.
  if (description) {
    upsertJsonLd('mybo-ld-speakable', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      url: canonical,
      description,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.tagline', '[data-seo-description]', 'meta[name=description]'],
      },
    })
  }

  if (input.imageUrl) {
    upsertJsonLd('mybo-ld-image', {
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      contentUrl: input.imageUrl,
      name: title,
    })
  }

  applySiteWebManifest({
    name: input.siteName || title,
    description,
    themeColor: input.themeColor || '#0a0a0a',
    startUrl: canonical || '/',
  })
}

export type ProductOfferInput = {
  id: string
  sku?: string
  name: string
  description?: string
  price: number
  currency?: string
  stockQty?: number
  coverImageUrl?: string | null
}

/** ItemList of Product/Offer for storefront AEO (prompt-seeded SKUs included). */
export function applyProductOffersJsonLd(
  products: ProductOfferInput[],
  opts?: { siteName?: string; canonicalUrl?: string },
) {
  const id = 'mybo-ld-products'
  if (!products || products.length === 0) {
    document.getElementById(id)?.remove()
    return
  }
  const canonical = opts?.canonicalUrl || (typeof location !== 'undefined' ? `${location.origin}/` : '')
  const list = products.slice(0, 24).map((p, i) => {
    const availability =
      (p.stockQty ?? 1) > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock'
    const rating = parseAggregateRatingFromText(p.description || '')
    return {
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Product',
        name: p.name,
        sku: p.sku || p.id,
        description: (p.description || p.name).trim(),
        ...(p.coverImageUrl ? { image: p.coverImageUrl } : {}),
        brand: opts?.siteName
          ? { '@type': 'Brand', name: opts.siteName }
          : undefined,
        ...(rating
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: rating.ratingValue,
                bestRating: rating.bestRating,
                ...(rating.reviewCount != null ? { reviewCount: rating.reviewCount } : {}),
              },
            }
          : {}),
        offers: {
          '@type': 'Offer',
          url: canonical,
          priceCurrency: (p.currency || 'TWD').toUpperCase(),
          price: Number.isFinite(p.price) ? p.price : 0,
          availability,
        },
      },
    }
  })
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: opts?.siteName ? `${opts.siteName} products` : 'Products',
    numberOfItems: list.length,
    itemListElement: list,
  })
}

/** ImageGallery of ImageObject from public albums (prompt package media). */
export function applyAlbumImagesJsonLd(
  albums: {
    title: string
    description?: string | null
    assets: { url: string; title?: string | null }[]
  }[],
  opts?: { siteName?: string },
) {
  const id = 'mybo-ld-images'
  const images = (albums || [])
    .flatMap((a) =>
      (a.assets || [])
        .filter((x) => !!x.url)
        .map((x) => ({
          url: x.url,
          name: (x.title || a.title || 'Image').trim(),
          description: (a.description || a.title || '').trim(),
        })),
    )
    .slice(0, 24)
  if (images.length === 0) {
    document.getElementById(id)?.remove()
    return
  }
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: opts?.siteName ? `${opts.siteName} gallery` : 'Gallery',
    associatedMedia: images.map((img) => ({
      '@type': 'ImageObject',
      contentUrl: img.url,
      url: img.url,
      name: img.name,
      ...(img.description ? { description: img.description } : {}),
    })),
  })
}

/** ItemList of VideoObject from public videos (package / YouTube embeds). */
export function applyVideoObjectsJsonLd(
  items: { id?: string; slug?: string; title: string; bodyHtml?: string | null }[],
  opts?: { siteName?: string; canonicalUrl?: string },
) {
  const id = 'mybo-ld-videos'
  const list = (items || [])
    .filter((v) => (v.title || '').trim().length >= 2)
    .slice(0, 12)
  if (list.length === 0) {
    document.getElementById(id)?.remove()
    return
  }
  const base = (opts?.canonicalUrl || (typeof location !== 'undefined' ? `${location.origin}/` : '/')).replace(/\/$/, '')
  const embedUrl = (html?: string | null) => {
    if (!html) return undefined
    const m =
      html.match(/src=["'](https?:\/\/(?:www\.)?youtube\.com\/embed\/[^"']+)["']/i)
      || html.match(/src=["'](https?:\/\/[^"']+\.(?:mp4|webm))["']/i)
    return m?.[1]
  }
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: opts?.siteName ? `${opts.siteName} videos` : 'Videos',
    numberOfItems: list.length,
    itemListElement: list.map((v, i) => {
      const contentUrl = embedUrl(v.bodyHtml)
      return {
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'VideoObject',
          name: v.title.trim(),
          description: v.title.trim(),
          url: `${base}/#${v.slug || 'video'}`,
          ...(contentUrl ? { embedUrl: contentUrl, contentUrl } : {}),
          publisher: opts?.siteName
            ? { '@type': 'Organization', name: opts.siteName }
            : undefined,
        },
      }
    }),
  })
}

/** ItemList of NewsArticle from public news / announces (prompt-seeded headlines). */
export function applyNewsArticlesJsonLd(
  items: { id?: string; slug?: string; title: string; bodyHtml?: string | null }[],
  opts?: { siteName?: string; canonicalUrl?: string },
) {
  const id = 'mybo-ld-news'
  const list = (items || [])
    .filter((n) => (n.title || '').trim().length >= 2)
    .slice(0, 12)
  if (list.length === 0) {
    document.getElementById(id)?.remove()
    return
  }
  const base = (opts?.canonicalUrl || (typeof location !== 'undefined' ? `${location.origin}/` : '/')).replace(/\/$/, '')
  const strip = (html?: string | null) => {
    if (!html) return ''
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  }
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: opts?.siteName ? `${opts.siteName} news` : 'News',
    numberOfItems: list.length,
    itemListElement: list.map((n, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'NewsArticle',
        headline: n.title.trim(),
        description: strip(n.bodyHtml).slice(0, 240) || n.title.trim(),
        url: `${base}/#${n.slug || 'news'}`,
        author: opts?.siteName
          ? { '@type': 'Organization', name: opts.siteName }
          : undefined,
        publisher: opts?.siteName
          ? { '@type': 'Organization', name: opts.siteName }
          : undefined,
      },
    })),
  })
}

/** BreadcrumbList from CMS pages (prompt-seeded contact/features/team/specs included). */
export function applyBreadcrumbListJsonLd(
  pages: { slug: string; title: string; isHome?: boolean }[],
  opts?: { canonicalUrl?: string; siteName?: string },
) {
  const id = 'mybo-ld-breadcrumb'
  const base = (opts?.canonicalUrl || (typeof location !== 'undefined' ? `${location.origin}/` : '/')).replace(/\/$/, '')
  const preferred = ['home', 'features', 'specs', 'team', 'contact', 'about']
  const bySlug = new Map((pages || []).map((p) => [p.slug.toLowerCase(), p]))
  const ordered: { slug: string; title: string }[] = []
  for (const slug of preferred) {
    const p = bySlug.get(slug)
    if (p) ordered.push({ slug: p.slug, title: p.title })
  }
  for (const p of pages || []) {
    if (preferred.includes(p.slug.toLowerCase())) continue
    if (p.slug.toLowerCase() === 'seo') continue
    if (ordered.some((o) => o.slug === p.slug)) continue
    ordered.push({ slug: p.slug, title: p.title })
  }
  if (ordered.length === 0) {
    document.getElementById(id)?.remove()
    return
  }
  const home = ordered.find((p) => p.slug.toLowerCase() === 'home') || ordered[0]
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: opts?.siteName || home.title,
      item: `${base}/`,
    },
    ...ordered
      .filter((p) => p.slug.toLowerCase() !== 'home')
      .slice(0, 8)
      .map((p, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: p.title,
        item: `${base}/#${p.slug}`,
      })),
  ]
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  })
}

/** FAQPage from prompt-seeded features / specs (AEO). */
export function applyFaqPageJsonLd(
  entries: { question: string; answer: string }[],
  opts?: { siteName?: string },
) {
  const id = 'mybo-ld-faq'
  const clean = (entries || [])
    .map((e) => ({
      question: (e.question || '').trim(),
      answer: (e.answer || '').trim(),
    }))
    .filter((e) => e.question.length >= 2 && e.answer.length >= 2)
    .slice(0, 12)
  if (clean.length === 0) {
    document.getElementById(id)?.remove()
    return
  }
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    name: opts?.siteName ? `${opts.siteName} FAQ` : 'FAQ',
    mainEntity: clean.map((e) => ({
      '@type': 'Question',
      name: e.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: e.answer,
      },
    })),
  })
}

/** ItemList of Person from prompt-seeded team page (AEO). */
export function applyTeamPersonsJsonLd(
  members: { role: string; bio?: string }[],
  opts?: { siteName?: string; canonicalUrl?: string },
) {
  const id = 'mybo-ld-team'
  const clean = (members || [])
    .map((m) => ({
      role: (m.role || '').trim(),
      bio: (m.bio || '').trim(),
    }))
    .filter((m) => m.role.length >= 2)
    .slice(0, 12)
  if (clean.length === 0) {
    document.getElementById(id)?.remove()
    return
  }
  const base = (opts?.canonicalUrl || (typeof location !== 'undefined' ? `${location.origin}/` : '/')).replace(/\/$/, '')
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: opts?.siteName ? `${opts.siteName} team` : 'Team',
    numberOfItems: clean.length,
    itemListElement: clean.map((m, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Person',
        name: m.role,
        jobTitle: m.role,
        ...(m.bio ? { description: m.bio } : {}),
        ...(opts?.siteName
          ? { worksFor: { '@type': 'Organization', name: opts.siteName } }
          : {}),
        url: `${base}/#team`,
      },
    })),
  })
}

/** HowTo from prompt-seeded feature steps (AEO / AGEO). */
export function applyHowToJsonLd(
  steps: { title: string; description?: string }[],
  opts?: { siteName?: string; name?: string; canonicalUrl?: string },
) {
  const id = 'mybo-ld-howto'
  const clean = (steps || [])
    .map((s) => ({
      title: (s.title || '').trim(),
      description: (s.description || '').trim(),
    }))
    .filter((s) => s.title.length >= 2)
    .slice(0, 12)
  if (clean.length < 2) {
    document.getElementById(id)?.remove()
    return
  }
  const canonical = opts?.canonicalUrl || (typeof location !== 'undefined' ? `${location.origin}/` : '')
  const name =
    (opts?.name || '').trim()
    || (opts?.siteName ? `${opts.siteName} highlights` : 'How to get started')
  upsertJsonLd(id, {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    url: canonical,
    ...(opts?.siteName ? { author: { '@type': 'Organization', name: opts.siteName } } : {}),
    step: clean.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.title,
      ...(s.description ? { text: s.description } : {}),
      url: `${canonical.replace(/\/$/, '')}/#features`,
    })),
  })
}

/** Per-site PWA manifest (blob URL) so prompt-owned sites get their own name/theme. */
export function applySiteWebManifest(input: {
  name: string
  description?: string
  themeColor?: string
  startUrl?: string
}) {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return
  const name = (input.name || 'Site').trim() || 'Site'
  const shortName = name.length > 12 ? `${name.slice(0, 11)}…` : name
  const theme = input.themeColor || '#0a0a0a'
  const manifest = {
    name,
    short_name: shortName,
    description: (input.description || name).trim(),
    start_url: input.startUrl || '/',
    display: 'standalone',
    background_color: theme,
    theme_color: theme,
    icons: [
      { src: '/favicon.ico', type: 'image/x-icon', sizes: '16x16 32x32' },
      { src: '/icon-192.png', type: 'image/png', sizes: '192x192' },
      { src: '/icon-512.png', type: 'image/png', sizes: '512x512' },
      { src: '/icon-192-maskable.png', type: 'image/png', sizes: '192x192', purpose: 'maskable' },
      { src: '/icon-512-maskable.png', type: 'image/png', sizes: '512x512', purpose: 'maskable' },
    ],
  }
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
  const url = URL.createObjectURL(blob)
  let link = document.head.querySelector('link[rel="manifest"]') as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = 'manifest'
    document.head.appendChild(link)
  } else if (link.href.startsWith('blob:')) {
    try { URL.revokeObjectURL(link.href) } catch { /* ignore */ }
  }
  link.href = url
}

export function extractSeoFromHtml(html: string): { title?: string; description?: string } {
  const title = html.match(/data-seo-title="([^"]*)"/i)?.[1]
  const description = html.match(/data-seo-description="([^"]*)"/i)?.[1]
  return {
    title: title ? decodeHtml(title) : undefined,
    description: description ? decodeHtml(description) : undefined,
  }
}

/** Parse prompt-seeded contact CMS HTML (`data-prompt-contact`). */
export function extractContactFromHtml(html: string): {
  address?: string
  telephone?: string
  email?: string
  openingHours?: string
} {
  if (!html || !/data-prompt-contact/i.test(html)) return {}
  const address = html.match(/class="address"[^>]*>([^<]+)</i)?.[1]
  const telephone =
    html.match(/href="tel:([^"]+)"/i)?.[1]
    || html.match(/class="phone"[^>]*>.*?>([^<]+)</i)?.[1]
  const email =
    html.match(/href="mailto:([^"]+)"/i)?.[1]
    || html.match(/class="email"[^>]*>.*?>([^<]+)</i)?.[1]
  const openingHours = html.match(/class="hours"[^>]*>([^<]+)</i)?.[1]
  return {
    address: address ? decodeHtml(address.trim()) : undefined,
    telephone: telephone ? decodeHtml(telephone.trim()) : undefined,
    email: email ? decodeHtml(email.trim()) : undefined,
    openingHours: openingHours ? decodeHtml(openingHours.trim()) : undefined,
  }
}

/** Parse prompt-seeded social links from contact CMS HTML. */
export function extractSocialFromHtml(html: string): { network: string; href: string }[] {
  if (!html || !/data-prompt-social/i.test(html)) return []
  const items: { network: string; href: string }[] = []
  const re = /<a[^>]*data-network="([^"]+)"[^>]*href="([^"]+)"[^>]*>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) {
    items.push({
      network: decodeHtml(m[1].trim()),
      href: decodeHtml(m[2].trim()),
    })
  }
  return items
}

/** Parse prompt-seeded specs table from CMS HTML. */
export function extractSpecsFromHtml(html: string): { label: string; value: string }[] {
  if (!html || !/data-prompt-specs/i.test(html)) return []
  const items: { label: string; value: string }[] = []
  const re = /<tr>\s*<th[^>]*>([^<]+)<\/th>\s*<td>([^<]+)<\/td>\s*<\/tr>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) {
    items.push({
      label: decodeHtml(m[1].trim()),
      value: decodeHtml(m[2].trim()),
    })
  }
  return items
}

/** Parse prompt-seeded team roles from CMS HTML. */
export function extractTeamFromHtml(html: string): { role: string; bio?: string }[] {
  if (!html || !/data-prompt-team/i.test(html)) return []
  const scope = html.match(/<ul[^>]*class="prompt-team"[^>]*>([\s\S]*?)<\/ul>/i)?.[1]
  if (!scope) return []
  const items: { role: string; bio?: string }[] = []
  const re = /<li[^>]*>\s*<strong>([^<]+)<\/strong>(?:\s*(?:—|–|-)\s*(?:<span>)?([^<]+))?/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(scope)) !== null) {
    const role = decodeHtml(m[1].trim())
    const bio = m[2] ? decodeHtml(m[2].trim()) : undefined
    if (role) items.push({ role, bio })
  }
  return items
}

/** Parse prompt-seeded CTAs from home CMS HTML. */
export function extractCtasFromHtml(html: string): { label: string; role: string; href: string }[] {
  if (!html || !/data-prompt-ctas/i.test(html)) return []
  const items: { label: string; role: string; href: string }[] = []
  const re = /<a[^>]*class="cta[^"]*"[^>]*data-cta="([^"]*)"[^>]*href="([^"]*)"[^>]*>([^<]+)<\/a>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) {
    items.push({
      role: decodeHtml(m[1].trim()),
      href: decodeHtml(m[2].trim()),
      label: decodeHtml(m[3].trim()),
    })
  }
  return items
}

/** Parse prompt-seeded feature list from CMS HTML. */
export function extractFeaturesFromHtml(html: string): { title: string; description?: string }[] {
  if (!html) return []
  const scope =
    html.match(/data-prompt-features[\s\S]*?<ul[^>]*class="prompt-features"[^>]*>([\s\S]*?)<\/ul>/i)?.[1]
    || html.match(/<ul[^>]*class="prompt-features"[^>]*>([\s\S]*?)<\/ul>/i)?.[1]
  if (!scope) return []
  const items: { title: string; description?: string }[] = []
  const re = /<li[^>]*>\s*<strong>([^<]+)<\/strong>(?:\s*(?:—|–|-)\s*(?:<span>)?([^<]+))?/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(scope)) !== null) {
    const title = decodeHtml(m[1].trim())
    const description = m[2] ? decodeHtml(m[2].trim()) : undefined
    if (title) items.push({ title, description })
  }
  return items
}

function decodeHtml(s: string) {
  const ta = document.createElement('textarea')
  ta.innerHTML = s
  return ta.value
}

/** Read `--color-accent: #RRGGBB` (etc.) from prompt `tokens.override.css`. */
export function extractCssVarHex(css: string, varName: string): string | undefined {
  const raw = extractCssVarValue(css, varName)
  const m = raw?.match(/#(?:[0-9A-Fa-f]{6})\b/)
  return m ? m[0].toUpperCase() : undefined
}

/** Read any `:root` custom property value from prompt `tokens.override.css`. */
export function extractCssVarValue(css: string, varName: string): string | undefined {
  if (!css || !varName) return undefined
  const escaped = varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const m = css.match(new RegExp(`${escaped}\\s*:\\s*([^;]+);`, 'i'))
  if (!m?.[1]) return undefined
  return m[1].trim().replace(/^['"]|['"]$/g, '')
}

/** Parse prompt blurb「★★★★★ (128 則評價)」into AggregateRating fields. */
export function parseAggregateRatingFromText(
  text: string,
): { ratingValue: number; bestRating: number; reviewCount?: number } | undefined {
  const stars = (text || '').match(/([★⭐]{1,5})/)
  if (!stars) return undefined
  const ratingValue = stars[1].length
  const count = (text || '').match(/(\d+)\s*則/)
  return {
    ratingValue,
    bestRating: 5,
    ...(count ? { reviewCount: Number(count[1]) } : {}),
  }
}

/** PostalAddress with TW hints for prompt-seeded contact pages. */
export function buildPostalAddress(address: string, telephone?: string): Record<string, unknown> {
  const street = address.trim()
  const out: Record<string, unknown> = {
    '@type': 'PostalAddress',
    streetAddress: street,
  }
  const twHint =
    /台灣|臺灣|台北|臺北|新北|台中|臺中|台南|臺南|高雄|桃園|新竹|Taiwan|Taipei|Kaohsiung/i.test(street)
    || /^\+?886\b/.test((telephone || '').trim())
    || /^0[2-8]\d{7,8}$/.test((telephone || '').replace(/[-\s]/g, ''))
  if (twHint) out.addressCountry = 'TW'
  const city = street.match(
    /(台北市|臺北市|新北市|桃園市|台中市|臺中市|台南市|臺南市|高雄市|新竹市|新竹縣|基隆市|嘉義市|宜蘭縣|花蓮縣|台東縣|臺東縣|屏東縣|彰化縣|雲林縣|南投縣|苗栗縣|澎湖縣|金門縣|連江縣)/,
  )?.[1]
  if (city) out.addressLocality = city
  return out
}

/** Structured hours from prompt defaults like「週一至週五 09:00–18:00」. */
export function parseOpeningHoursSpecifications(
  hours: string,
): Record<string, unknown>[] | undefined {
  const raw = (hours || '').trim()
  if (!raw) return undefined
  const m = raw.match(
    /週一至週[五六日天]\s*(\d{1,2}:\d{2})\s*[–\-〜~至到]\s*(\d{1,2}:\d{2})/,
  )
  if (!m) return undefined
  // 週一至週五 → Mon–Fri; 週一至週六 → Mon–Sat; 週一至週日/天 → all week
  let days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  if (/週一至週六/.test(raw)) {
    days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  } else if (/週一至週[日天]/.test(raw)) {
    days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  }
  return [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: days,
      opens: normalizeHour(m[1]),
      closes: normalizeHour(m[2]),
    },
  ]
}

function normalizeHour(h: string): string {
  const [hh, mm] = h.split(':')
  return `${hh.padStart(2, '0')}:${mm}`
}
